import { useCallback, useEffect, useState } from "react";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Runs `fetcher` on mount (and whenever `deps` changes), tracking loading/data/error so screens
 * don't each hand-roll the same three `useState` calls. Exposes `reload` for retry buttons and
 * post-mutation refreshes (e.g. re-fetching admins after adding one).
 */
export function useAsyncData<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });

  const load = useCallback(() => {
    setState((previous) => ({ ...previous, loading: true, error: null }));
    fetcher()
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((error: unknown) => setState({ data: null, loading: false, error: toError(error) }));
    // `fetcher` is intentionally excluded: call sites pass a fresh closure each render, and
    // depending on it would defeat `deps` as the actual re-fetch trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}
